import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, CheckCircle, Lock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent, AnalyticsEvents } from "@/lib/analytics";
import posthog from "posthog-js";

type WaitlistState = "idle" | "submitting" | "success" | "already_joined";

const ProWaitlist = () => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<WaitlistState>("idle");
  const [error, setError] = useState("");

  const validateEmail = (email: string): boolean => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email.trim());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedEmail = email.trim().toLowerCase();

    if (!validateEmail(trimmedEmail)) {
      setError("Please enter a valid email address");
      return;
    }

    setError("");
    posthog.capture('audit_request_initiated');
    setState("submitting");

    try {
      const { data, error: fnError } = await supabase.functions.invoke("join-waitlist", {
        body: { email: trimmedEmail },
      });

      if (fnError) {
        // Try to parse the response body for status
        let responseBody: any = null;
        try {
          const maybeResponse = (fnError as any)?.context;
          if (maybeResponse && typeof maybeResponse.clone === "function") {
            responseBody = await (maybeResponse as Response).clone().json();
          }
        } catch { /* ignore */ }

        if (responseBody?.status === "already_joined") {
          setState("already_joined");
          return;
        }

        throw new Error(responseBody?.error || fnError.message || "Something went wrong");
      }

      if (data?.status === "already_joined") {
        setState("already_joined");
        return;
      }

      trackEvent(AnalyticsEvents.WAITLIST_SIGNUP, { waitlist: true });
      setState("success");
    } catch (err: any) {
      console.error("Waitlist signup error:", err);
      setError(err.message || "Something went wrong. Please try again.");
      setState("idle");
    }
  };

  if (state === "success") {
    return (
      <Card id="pro-waitlist" className="w-full max-w-2xl mx-auto bg-secondary/30 border-border shadow-[2px_2px_8px_rgba(0,0,0,0.25)] animate-fade-in">
        <CardContent className="pt-8 pb-8 text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-success/20 flex items-center justify-center mx-auto">
            <CheckCircle className="w-8 h-8 text-success" />
          </div>
          <h3 className="text-xl font-bold text-foreground">You're on the list.</h3>
          <p className="text-sm text-muted-foreground">We'll notify you at launch.</p>
        </CardContent>
      </Card>
    );
  }

  if (state === "already_joined") {
    return (
      <Card id="pro-waitlist" className="w-full max-w-2xl mx-auto bg-secondary/30 border-border shadow-[2px_2px_8px_rgba(0,0,0,0.25)] animate-fade-in">
        <CardContent className="pt-8 pb-8 text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto">
            <CheckCircle className="w-8 h-8 text-primary" />
          </div>
          <h3 className="text-xl font-bold text-foreground">You're already on the list!</h3>
          <p className="text-sm text-muted-foreground">We'll notify you when Pro launches.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card id="pro-waitlist" className="w-full max-w-2xl mx-auto bg-secondary/30 border-border shadow-[2px_2px_8px_rgba(0,0,0,0.25)] animate-fade-in">
      <CardContent className="pt-8 pb-8 space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <h3 className="text-2xl font-bold text-foreground">
            Get a Full AI Visibility Audit
          </h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            A deeper look at how AI platforms see your brand, with practical next steps.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
          <Input
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError(""); }}
            className="h-12 text-base flex-1"
            disabled={state === "submitting"}
            required
          />
          <Button
            type="submit"
            size="lg"
            className="h-12 px-8 font-semibold"
            disabled={state === "submitting" || !email.trim()}
          >
            {state === "submitting" ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              "Request Full Audit"
            )}
          </Button>
        </form>

        {error && (
          <p className="text-sm text-destructive text-center">{error}</p>
        )}

        {/* Trust line */}
        <p className="text-xs text-muted-foreground text-center flex items-center justify-center gap-1.5">
          <Lock className="w-3 h-3" />
          No spam. We'll follow up with audit details.
        </p>
      </CardContent>
    </Card>
  );
};

export default ProWaitlist;
